import type { TrafficManager } from '../traffic/TrafficManager';

export interface RecordedAgentPose {
  x: number;
  y: number;
  z: number;
  rotY: number;
}

export interface TelemetryFrame {
  time: number; // in seconds
  position: [number, number, number];
  quaternion: [number, number, number, number];
  speedKmh: number;
  steeringAngle: number;
  throttle: number;
  brake: number;
  clutch: number;
  handbrake: boolean;
  rpm: number;
  gear: string;
  blinker: 'left' | 'right' | 'hazard' | 'off';
  shoulderCheck: string | null;
  mirrorLook: string | null;
  infractionTitle: string | null;
  aiVehicles?: RecordedAgentPose[];
  busPose?: RecordedAgentPose | null;
  cyclists?: RecordedAgentPose[];
  pedestrians?: RecordedAgentPose[];
}

export class TelemetryRecorder {
  public frames: TelemetryFrame[] = [];
  public maxDurationSeconds = 180; // 3 minutes buffer
  private recordInterval = 1 / 60; // 60 Hz recording
  private timer = 0;

  public static captureTrafficSnapshot(traffic?: TrafficManager): Pick<
    TelemetryFrame,
    'aiVehicles' | 'busPose' | 'cyclists' | 'pedestrians'
  > {
    if (!traffic) return {};
    const r = (n: number) => Math.round(n * 100) / 100;
    return {
      aiVehicles: traffic.vehicles.map((v) => ({
        x: r(v.position.x),
        y: r(v.position.y),
        z: r(v.position.z),
        rotY: r(v.mesh.rotation.y),
      })),
      busPose: traffic.bus
        ? {
            x: r(traffic.bus.position.x),
            y: r(traffic.bus.position.y),
            z: r(traffic.bus.position.z),
            rotY: r(traffic.bus.mesh.rotation.y),
          }
        : null,
      cyclists: traffic.cyclists.map((c) => ({
        x: r(c.position.x),
        y: r(c.position.y),
        z: r(c.position.z),
        rotY: r(c.mesh.rotation.y),
      })),
      pedestrians: traffic.pedestrians.map((p) => ({
        x: r(p.position.x),
        y: r(p.position.y),
        z: r(p.position.z),
        rotY: r(p.mesh.rotation.y),
      })),
    };
  }

  public clear() {
    this.frames = [];
    this.timer = 0;
  }

  public record(deltaTime: number, frameData: Omit<TelemetryFrame, 'time'>, missionTime: number) {
    this.timer += deltaTime;
    if (this.timer >= this.recordInterval) {
      this.timer = 0;

      const frame: TelemetryFrame = {
        time: Math.round(missionTime * 100) / 100,
        ...frameData,
      };

      this.frames.push(frame);

      // Enforce max duration
      const maxFrames = this.maxDurationSeconds * 60;
      if (this.frames.length > maxFrames) {
        this.frames.shift();
      }
    }
  }

  public getDuration(): number {
    if (this.frames.length < 2) return 0;
    return this.frames[this.frames.length - 1].time - this.frames[0].time;
  }
}
